import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuthState } from '@/hooks/useAuth'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, RefreshCw, ChevronRight } from 'lucide-react'
import { CORE_ANALYTICS_ENDPOINT } from '@/config/endpoints'
import { REGION_DEFINITIONS } from '@/lib/locationRegions'
import type { CoreAnalyticsResponse } from '@/types/models'

type TimeframeDays = 7 | 30
type CountryFilterValue = 'all' | 'FI' | 'DE' | 'NL' | 'EE'

const FILTER_TRIGGER_CLASS =
  'h-auto min-h-0 py-1.5 !text-xs sm:!text-xs text-muted-foreground font-normal border-border/60 bg-transparent shadow-none [&>span]:!text-xs'

function formatChangePercent(changePercent: number | null): string {
  if (changePercent === null) return '—'
  const rounded = Math.round(changePercent)
  if (rounded > 0) return `+${rounded}%`
  if (rounded < 0) return `${rounded}%`
  return '0%'
}

function getChangeColor(changePercent: number | null): string {
  if (changePercent === null || changePercent === 0) return 'text-muted-foreground'
  return changePercent > 0 ? 'text-emerald-500' : 'text-red-500'
}

function getCountryLabel(countryIso2: CountryFilterValue): string {
  if (countryIso2 === 'all') return 'All countries'
  return REGION_DEFINITIONS.find((region) => region.iso2 === countryIso2)?.countryName ?? countryIso2
}

function getTimeframeLabel(timeframeDays: TimeframeDays): string {
  return timeframeDays === 7 ? 'Last 7 days' : 'Last 30 days'
}

function filtersMatchLoadedData(
  analyticsData: CoreAnalyticsResponse | null,
  timeframeDays: TimeframeDays,
  countryIso2: CountryFilterValue,
): boolean {
  if (!analyticsData) return false
  return (
    analyticsData.timeframe.lastXDays === timeframeDays &&
    analyticsData.timeframe.countryIso2 === countryIso2
  )
}

function DetailMetricCell({
  value,
  label,
}: {
  value: ReactNode
  label: string
}) {
  return (
    <div className="min-w-0">
      <div className="text-lg font-semibold tabular-nums leading-tight mb-0.5">{value}</div>
      <p className="text-[11px] text-muted-foreground leading-snug">{label}</p>
    </div>
  )
}

function DetailMetricsGrid({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border pt-2 mt-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">{children}</div>
    </div>
  )
}

function ArtistsTotalWithBookingValue({
  totalArtists,
  artistsWithBooking,
}: {
  totalArtists: number
  artistsWithBooking: number
}) {
  return (
    <span className="inline-flex items-baseline gap-0.5">
      <span className="text-foreground">{totalArtists.toLocaleString()}</span>
      <span className="text-muted-foreground text-base">(</span>
      <span className="text-pink-500">{artistsWithBooking.toLocaleString()}</span>
      <span className="text-muted-foreground text-base">)</span>
    </span>
  )
}

function MetricCard({
  title,
  value,
  subtitle,
  changePercent,
  accentColor,
  detail,
}: {
  title: string
  value: number
  subtitle: string
  changePercent: number | null
  accentColor: string
  detail?: ReactNode
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <div className="flex items-end gap-2 mb-1.5">
        <div className="inline-flex items-start leading-none shrink-0">
          <span className={`text-4xl font-bold tabular-nums tracking-tight ${accentColor}`}>
            {value.toLocaleString()}
          </span>
          <span
            className={`text-[0.65rem] font-semibold tabular-nums leading-none ml-0.5 mt-0.5 ${getChangeColor(changePercent)}`}
          >
            {formatChangePercent(changePercent)}
          </span>
        </div>
        <h3 className={`text-sm font-semibold pb-0.5 leading-tight ${accentColor}`}>{title}</h3>
      </div>

      <p className="text-xs text-muted-foreground leading-snug">{subtitle}</p>

      {detail}
    </div>
  )
}

export default function CoreAnalyticsPage() {
  const { user } = useAuthState()
  const [analyticsData, setAnalyticsData] = useState<CoreAnalyticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedTimeframeDays, setSelectedTimeframeDays] = useState<TimeframeDays>(7)
  const [selectedCountryIso2, setSelectedCountryIso2] = useState<CountryFilterValue>('all')

  const fetchAnalytics = useCallback(
    async (lastXDays: TimeframeDays, countryIso2: CountryFilterValue, abortSignal?: AbortSignal) => {
      setIsLoading(true)
      setError(null)

      try {
        if (!user) {
          setError('Not authenticated')
          return
        }

        const idToken = await user.getIdToken()

        const response = await fetch(CORE_ANALYTICS_ENDPOINT, {
          method: 'POST',
          signal: abortSignal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ lastXDays, countryIso2 }),
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch analytics: ${response.statusText}`)
        }

        const data: CoreAnalyticsResponse = await response.json()
        setAnalyticsData(data)
      } catch (fetchError) {
        if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
          return
        }
        console.error('Error fetching core analytics:', fetchError)
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to fetch analytics')
      } finally {
        if (!abortSignal?.aborted) {
          setIsLoading(false)
        }
      }
    },
    [user],
  )

  useEffect(() => {
    if (!user) return

    const abortController = new AbortController()
    void fetchAnalytics(selectedTimeframeDays, selectedCountryIso2, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [user, selectedTimeframeDays, selectedCountryIso2, fetchAnalytics])

  const formatCacheAge = (cachedAt: string) => {
    const ageMinutes = Math.round((Date.now() - new Date(cachedAt).getTime()) / 60000)
    if (ageMinutes < 1) return 'just now'
    if (ageMinutes === 1) return '1 min ago'
    return `${ageMinutes} min ago`
  }

  const comparisonLabel = selectedTimeframeDays === 7 ? 'vs prior 7 days' : 'vs prior 30 days'
  const selectedCountryLabel = getCountryLabel(selectedCountryIso2)
  const selectedTimeframeLabel = getTimeframeLabel(selectedTimeframeDays)
  const isDataFresh = filtersMatchLoadedData(analyticsData, selectedTimeframeDays, selectedCountryIso2)
  const isInitialLoading = isLoading && !analyticsData
  const isUpdatingMetrics = isLoading && analyticsData != null

  const filterStatusMessage = isInitialLoading
    ? 'Loading metrics…'
    : isUpdatingMetrics
      ? `Updating · ${selectedTimeframeLabel} · ${selectedCountryLabel}`
      : null

  return (
    <AdminAuthGuard>
      <div className="h-[100dvh] flex flex-col bg-background overflow-hidden pt-[env(safe-area-inset-top)] pb-bottom-nav">
        <div className="phone-container-padded flex flex-col flex-1 min-h-0 py-3">
          <div className="shrink-0 mb-2">
            <h1 className="text-xl font-bold text-foreground leading-tight">Pulse</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{comparisonLabel}</p>

            <div className="grid grid-cols-2 gap-2 mt-2">
              <Select
                value={String(selectedTimeframeDays)}
                onValueChange={(value) => setSelectedTimeframeDays(Number(value) as TimeframeDays)}
              >
                <SelectTrigger className={FILTER_TRIGGER_CLASS}>
                  <SelectValue placeholder="Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7" className="text-xs">
                    Last 7 days
                  </SelectItem>
                  <SelectItem value="30" className="text-xs">
                    Last 30 days
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={selectedCountryIso2}
                onValueChange={(value) => setSelectedCountryIso2(value as CountryFilterValue)}
              >
                <SelectTrigger className={FILTER_TRIGGER_CLASS}>
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">
                    All countries
                  </SelectItem>
                  {REGION_DEFINITIONS.map((region) => (
                    <SelectItem key={region.iso2} value={region.iso2} className="text-xs">
                      {region.countryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLoading && filterStatusMessage && (
              <div
                className="flex items-center gap-1.5 mt-1.5"
                aria-live="polite"
                aria-atomic="true"
              >
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="text-xs text-muted-foreground">{filterStatusMessage}</span>
              </div>
            )}
          </div>

          {isInitialLoading && (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-destructive/50 bg-destructive/5 p-3 mb-2 shrink-0">
              <p className="text-destructive text-xs">{error}</p>
              <Button
                onClick={() => fetchAnalytics(selectedTimeframeDays, selectedCountryIso2)}
                variant="outline"
                size="sm"
                className="mt-2 h-7 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {analyticsData && (
            <div className="flex flex-col flex-1 min-h-0">
              <div
                className={`shrink-0 flex flex-col gap-2 transition-opacity duration-200 ${
                  isUpdatingMetrics ? 'opacity-45 pointer-events-none' : 'opacity-100'
                }`}
              >
                <MetricCard
                  title="Active users"
                  value={analyticsData.activeUsers.current.total}
                  subtitle="Saved, followed, chatted, messaged, or used matcher"
                  changePercent={analyticsData.activeUsers.changePercent}
                  accentColor="text-blue-500"
                  detail={
                    <DetailMetricsGrid>
                      <DetailMetricCell
                        value={analyticsData.activeUsers.current.new?.toLocaleString() ?? '—'}
                        label="new"
                      />
                      <DetailMetricCell
                        value={analyticsData.activeUsers.current.returning?.toLocaleString() ?? '—'}
                        label="returning"
                      />
                    </DetailMetricsGrid>
                  }
                />

                <MetricCard
                  title="Active artists"
                  value={analyticsData.activeArtists.current.total}
                  subtitle="Uploaded work or sent a message"
                  changePercent={analyticsData.activeArtists.changePercent}
                  accentColor="text-pink-500"
                  detail={
                    <DetailMetricsGrid>
                      <DetailMetricCell
                        value={analyticsData.activeArtists.current.newOnboardings?.toLocaleString() ?? '—'}
                        label="new onboardings"
                      />
                      <DetailMetricCell
                        value={
                          <ArtistsTotalWithBookingValue
                            totalArtists={analyticsData.activeArtists.current.totalArtists ?? 0}
                            artistsWithBooking={analyticsData.activeArtists.current.artistsWithBooking ?? 0}
                          />
                        }
                        label="total artists (≥1 booking ever)"
                      />
                    </DetailMetricsGrid>
                  }
                />

                <MetricCard
                  title="Chats started"
                  value={analyticsData.chatsStarted.current.total}
                  subtitle="Unique users who started a chat, max 1 per day"
                  changePercent={analyticsData.chatsStarted.changePercent}
                  accentColor="text-emerald-500"
                />
              </div>

              <div className="flex-1 min-h-0" aria-hidden="true" />

              <div className="shrink-0 pt-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-muted-foreground">
                    {isDataFresh
                      ? analyticsData.fromCache
                        ? `Cached ${formatCacheAge(analyticsData.cachedAt)}`
                        : `Computed in ${analyticsData.computedInSeconds}s`
                      : 'Waiting for updated metrics…'}
                  </p>
                  <Button
                    onClick={() => fetchAnalytics(selectedTimeframeDays, selectedCountryIso2)}
                    disabled={isLoading}
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground"
                    aria-label="Refresh metrics"
                  >
                    {isLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>

                <Link
                  to="/analytics/detailed"
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>Detailed analytics</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminAuthGuard>
  )
}
